/**
 * Persistência das configurações de áudio (volumes + mute).
 *
 * Vai pra `localStorage` por dois motivos:
 *  1. Acesso síncrono — AudioManager precisa ler antes do primeiro frame.
 *  2. Volume é "global ao dispositivo", não vinculado ao perfil do jogador.
 *
 * Em testes ou ambientes sem localStorage, MemorySettingsStore mantém os
 * valores em memória.
 */

export interface AudioSettings {
  /** 0..1 */
  musicVolume: number;
  /** 0..1 */
  sfxVolume: number;
  /** 0..1 (reservado para falas em PT-BR — futuro). */
  voiceVolume: number;
  muted: boolean;
}

export const DEFAULT_AUDIO_SETTINGS: AudioSettings = {
  musicVolume: 0.6,
  sfxVolume: 0.8,
  voiceVolume: 0.8,
  muted: false,
};

export interface SettingsStore {
  load(): AudioSettings | null;
  save(settings: AudioSettings): void;
}

const STORAGE_KEY = 'tabuada-geek:audio-settings';

export class LocalStorageSettingsStore implements SettingsStore {
  load(): AudioSettings | null {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw) as Partial<AudioSettings>;
      return mergeWithDefaults(parsed);
    } catch {
      return null;
    }
  }

  save(settings: AudioSettings): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
    } catch {
      // Modo privado restrito ou cota cheia: degrada silenciosamente.
    }
  }
}

export class MemorySettingsStore implements SettingsStore {
  private current: AudioSettings | null = null;

  load(): AudioSettings | null {
    return this.current ? { ...this.current } : null;
  }

  save(settings: AudioSettings): void {
    this.current = { ...settings };
  }
}

/** Merge defensivo: campo ausente cai no default, valores fora do range são clampados. */
function mergeWithDefaults(partial: Partial<AudioSettings>): AudioSettings {
  return {
    musicVolume: clamp01(partial.musicVolume ?? DEFAULT_AUDIO_SETTINGS.musicVolume),
    sfxVolume: clamp01(partial.sfxVolume ?? DEFAULT_AUDIO_SETTINGS.sfxVolume),
    voiceVolume: clamp01(partial.voiceVolume ?? DEFAULT_AUDIO_SETTINGS.voiceVolume),
    muted: typeof partial.muted === 'boolean' ? partial.muted : DEFAULT_AUDIO_SETTINGS.muted,
  };
}

function clamp01(v: number): number {
  if (Number.isNaN(v)) return 0;
  return Math.max(0, Math.min(1, v));
}

export function createDefaultSettingsStore(): SettingsStore {
  if (typeof localStorage === 'undefined') {
    return new MemorySettingsStore();
  }
  return new LocalStorageSettingsStore();
}
